// frontend/src/services/api.js
import axios from 'axios';

const api = axios.create({
    baseURL: process.env.REACT_APP_API_URL || '/api/v1',
    // NOTE: No default Content-Type here.
    // It is chosen per request in the interceptor below so that
    // multipart/form-data (file uploads) works correctly.
    timeout: 60000,
});

// Request interceptor
api.interceptors.request.use(
    (config) => {
        const token = localStorage.getItem('token');
        if (token) {
            config.headers = config.headers || {};
            config.headers.Authorization = `Bearer ${token}`;
        }

        // If the body is FormData, the browser must be allowed to set
        // Content-Type itself so it includes the multipart boundary.
        // Remove any Content-Type header that may have been set previously.
        if (typeof FormData !== 'undefined' && config.data instanceof FormData) {
            if (config.headers) {
                delete config.headers['Content-Type'];
                delete config.headers['content-type'];
            }
        } else if (
            config.headers &&
            !config.headers['Content-Type'] &&
            !config.headers['content-type']
        ) {
            // Default to JSON for non-FormData bodies.
            config.headers['Content-Type'] = 'application/json';
        }

        console.log(`[API Request] ${config.method?.toUpperCase()} ${config.url}`);
        return config;
    },
    (error) => {
        console.error('[API Request Error]', error);
        return Promise.reject(error);
    }
);

// Response interceptor
api.interceptors.response.use(
    (response) => {
        if (response.config.url?.includes('/auth/login')) {
            console.log(`[API Response] ${response.status} ${response.config.url}`);
        } else {
            console.log(`[API Response] ${response.status} ${response.config.url}`, response.data);
        }
        return response;
    },
    (error) => {
        console.error('[API Error]', error.response?.status, error.response?.data || error.message);

        // Handle 401 Unauthorized
        if (error.response?.status === 401) {
            const currentPath = window.location.pathname;
            const isPasswordSetup = error.config?.url?.includes('/auth/first-login-password');
            if (!currentPath.includes('/login') && !currentPath.includes('/register') && !isPasswordSetup) {
                localStorage.removeItem('token');
                localStorage.removeItem('user');
                window.location.href = '/login';
            }
        }

        // For login errors, we want to return the error so the component can handle it
        if (error.config?.url?.includes('/auth/login')) {
            return Promise.reject(error);
        }

        return Promise.reject(error);
    }
);

// ========== AUTH ENDPOINTS ==========
export const authAPI = {
    login: (credentials) => api.post('/auth/login', credentials),
    register: (data) => api.post('/auth/register', data),
    logout: () => {
        localStorage.removeItem('token');
        localStorage.removeItem('user');
    },
    getCurrentUser: () => api.get('/auth/me'),
};

// ========== QUOTE ENDPOINTS ==========
export const quoteAPI = {
    getAll: () => api.get('/quotes'),
    getById: (id) => api.get(`/quotes/${id}`),
    create: (data) => {
        const isFormData = typeof FormData !== 'undefined' && data instanceof FormData;
        return api.post('/quotes', data, {
            headers: isFormData
                ? { 'Content-Type': 'multipart/form-data' }
                : { 'Content-Type': 'application/json' },
        });
    },
    approve: (id) => api.put(`/quotes/${id}/approve`),
    reject: (id, data) => api.put(`/quotes/${id}/reject`, data),
    amend: (id, data) => api.put(`/quotes/${id}`, data),
    getPDF: (id) => api.get(`/quotes/${id}/pdf`, { responseType: 'blob' }),
    getAIRisk: (id) => api.get(`/quotes/${id}/ai-risk`),
    convertToCargo: (id) => api.post(`/quotes/${id}/convert-to-cargo`),
};

// ========== OPEN COVER ENDPOINTS ==========
export const openCoverAPI = {
    getAll: () => api.get('/quotes/open-covers'),
    getById: (id) => api.get(`/quotes/open-covers/${id}`),
    create: (data) => api.post('/quotes/open-covers', data),
    renew: (id) => api.post(`/quotes/open-covers/${id}/renew`),
    cancel: (id) => api.put(`/quotes/open-covers/${id}/cancel`),
    getHistory: (id) => api.get(`/quotes/open-covers/${id}/history`),
    getQuotesForCover: (coverId) => api.get(`/quotes?open_cover_id=${coverId}`),
};

// ========== CARGO ENDPOINTS ==========
export const cargoAPI = {
    idfLookup: (data) => api.post('/cargo/idf-lookup', data),
    createCover: (data) => api.post('/covers', data),
    getQuotes: (idfData) => api.get('/cargo/quotes', { params: idfData }),
    acceptQuote: (data) => api.post('/cargo/accept', data),
    makePayment: (data) => api.post('/cargo/payment', data),
    generateCertificate: (data) => api.post('/cargo/certificate', data),
};

// ========== VESSEL ENDPOINTS ==========
export const vesselAPI = {
    getAll: () => api.get('/vessels'),
    getById: (id) => api.get(`/vessels/${id}`),
    create: (data) => api.post('/vessels', data),
    update: (id, data) => api.put(`/vessels/${id}`, data),
    delete: (id) => api.delete(`/vessels/${id}`),
};

// ========== PRODUCT ENDPOINTS ==========
export const productAPI = {
    getAll: () => api.get('/products'),
    getById: (id) => api.get(`/products/${id}`),
    create: (data) => api.post('/products', data),
    update: (id, data) => api.put(`/products/${id}`, data),
    delete: (id) => api.delete(`/products/${id}`),
};

// ========== USER/CLIENT ENDPOINTS ==========
export const userAPI = {
    getAll: (params) => api.get('/users', { params }),
    getById: (id) => api.get(`/users/${id}`),
    create: (data) => api.post('/users', data),
    update: (id, data) => api.put(`/users/${id}`, data),
    delete: (id) => api.delete(`/users/${id}`),
    getClients: (search) => api.get(`/users?search=${search}&role=direct_client`),
};

// ========== CLEARING AGENT ENDPOINTS ==========
export const clearingAgentAPI = {
    getAll: () => api.get('/clearing-agents'),
    getById: (id) => api.get(`/clearing-agents/${id}`),
    create: (data) => api.post('/clearing-agents', data),
    update: (id, data) => api.put(`/clearing-agents/${id}`, data),
    delete: (id) => api.delete(`/clearing-agents/${id}`),
};

// ========== DOCUMENT ENDPOINTS ==========
export const documentAPI = {
    upload: (data) => api.post('/documents/upload', data),
    getById: (id) => api.get(`/documents/${id}`),
    delete: (id) => api.delete(`/documents/${id}`),
};

// ========== PAYMENT ENDPOINTS ==========
export const paymentAPI = {
    create: (data) => api.post('/payments', data),
    getById: (id) => api.get(`/payments/${id}`),
    getByQuote: (quoteId) => api.get(`/payments/quote/${quoteId}`),
};

// ========== DASHBOARD ENDPOINTS ==========
export const dashboardAPI = {
    getStats: () => api.get('/dashboard/stats'),
    getRecentActivity: () => api.get('/dashboard/activity'),
    getCharts: () => api.get('/dashboard/charts'),
};

// ========== NOTIFICATION ENDPOINTS ==========
export const notificationAPI = {
    getAll: (params) => api.get('/notifications', { params }),
    getUnreadCount: () => api.get('/notifications/unread/count'),
    markAsRead: (id) => api.put(`/notifications/${id}/read`),
    markAllAsRead: () => api.put('/notifications/read-all'),
    delete: (id) => api.delete(`/notifications/${id}`),
    getConnectionStatus: () => api.get('/notifications/status'),
};

// ========== DEFAULT EXPORT ==========
export default api;